import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ruler, X, Sparkles, PenLine, Wand2 } from "lucide-react";
import { MonogramPreviewModal } from "./MonogramPreviewModal";
import { PillSelect } from "./PillSelect";
import { SIZE_CHARTS } from "@/data/variationSchemas";
import { useSchemas } from "@/context/SchemasContext";

/**
 * VariationSelectors — dynamic PDP variant selectors driven by the category
 * schema. Reports every selection up via `onChange({...})` so the parent can
 * store it in the cart line and pipe it into WhatsApp / checkout / order.
 */
export const VariationSelectors = ({ product, value, onChange, formatPrice }) => {
  const { getSchema } = useSchemas();
  const schema = useMemo(() => getSchema(product.category), [product.category, getSchema]);
  const [chartOpen, setChartOpen] = useState(false);
  const [monogramModal, setMonogramModal] = useState(false);
  const monogramPrice = Number(product?.monogram_price || 0);

  const set = (patch) => onChange({ ...value, ...patch });

  // ---- Per-product sizes (with add-on pricing + inches labels) ----
  // If the product has its own `sizes` or `size_inches` arrays, we render them
  // above the schema-driven size block. Each button can carry a `+₹X` add-on.
  const productSizes = Array.isArray(product?.sizes) ? product.sizes : [];
  const sizeInches = Array.isArray(product?.size_inches) ? product.size_inches : [];
  const sizeAddons = Array.isArray(product?.size_addons) ? product.size_addons : [];
  const findAddon = (name) => {
    const hit = sizeAddons.find((a) => (a?.name || "").trim().toLowerCase() === (name || "").trim().toLowerCase());
    return Number(hit?.price || 0);
  };
  const pickProductSize = (name) => {
    const addon = findAddon(name);
    set({ size: name, customSize: "", sizeAddon: addon });
  };
  const productSizeSection = (productSizes.length || sizeInches.length) ? (() => {
    const opts = [];
    productSizes.forEach((s) => opts.push({ value: s, label: s, addon: findAddon(s) }));
    sizeInches.forEach((s) => {
      if (!opts.some((o) => o.value === s)) opts.push({ value: s, label: s, addon: findAddon(s) });
    });
    return (
      <div className="mt-6" data-testid="product-size-block">
        <PillSelect
          label={
            <span className="inline-flex items-center gap-1.5"><Ruler size={12} strokeWidth={1.6} /> Size</span>
          }
          placeholder="Select Size"
          value={value.size || ""}
          options={opts}
          onChange={pickProductSize}
          formatPrice={formatPrice}
          testid="product-size"
        />
      </div>
    );
  })() : null;

  // ---- sizes (schema-driven) ----
  const sizeSection = (() => {
    if (schema.sizes?.regions) {
      const regions = Object.keys(schema.sizes.regions);
      const region = value.sizeRegion || regions[0];
      const list = schema.sizes.regions[region] || [];
      const switchRegion = (r) => {
        const newList = schema.sizes.regions[r] || [];
        const keepSize = newList.includes(value.size);
        set({ sizeRegion: r, size: keepSize ? value.size : "" });
      };
      return (
        <div className="mt-6" data-testid="variation-size-block">
          <div className="overline text-forest/60 mb-2 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 tracking-[2px] text-[10px]"><Ruler size={12} strokeWidth={1.6} /> Size · {region}</span>
            {schema.sizeChart && (
              <button onClick={() => setChartOpen(true)} data-testid="size-chart-open" className="edit-link text-xs">Size chart ↗</button>
            )}
          </div>
          <div className="flex gap-2 mb-2 flex-wrap">
            {regions.map((r) => (
              <button
                key={r}
                data-testid={`size-region-${r}`}
                onClick={() => switchRegion(r)}
                className={`overline text-[10px] px-3 py-1 rounded-full border transition-colors ${
                  region === r ? "bg-forest text-white border-forest" : "text-forest/70 border-hairline"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <PillSelect
            placeholder="Select Size"
            value={value.size || ""}
            options={list.map((s) => ({ value: s, label: s }))}
            onChange={(s) => set({ size: s, customSize: "" })}
            testid="variation-size"
          />
          {schema.sizes.allowCustom && (
            <input
              data-testid="variation-size-custom"
              value={value.customSize || ""}
              onChange={(e) => set({ customSize: e.target.value, size: e.target.value ? "" : value.size })}
              placeholder='Custom size (e.g., 72" × 84")'
              className="mt-3 w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
            />
          )}
        </div>
      );
    }
    if (schema.sizes?.list) {
      return (
        <div className="mt-6" data-testid="variation-size-block">
          <PillSelect
            label={
              <span className="inline-flex items-center gap-1.5"><Ruler size={12} strokeWidth={1.6} /> Size</span>
            }
            trailing={
              schema.sizeChart ? (
                <button onClick={() => setChartOpen(true)} data-testid="size-chart-open" className="edit-link text-xs normal-case tracking-normal">Size chart ↗</button>
              ) : null
            }
            placeholder="Select Size"
            value={value.size || ""}
            options={schema.sizes.list.map((s) => ({ value: s, label: s }))}
            onChange={(s) => set({ size: s, customSize: "" })}
            testid="variation-size"
          />
          {schema.sizes.allowCustom && (
            <input
              data-testid="variation-size-custom"
              value={value.customSize || ""}
              onChange={(e) => set({ customSize: e.target.value, size: e.target.value ? "" : value.size })}
              placeholder="Custom size (share width × drop)"
              className="mt-3 w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
            />
          )}
        </div>
      );
    }
    return null;
  })();

  // ---- colours ----
  const productColours = Array.isArray(product?.colours) ? product.colours : [];
  const colourList = productColours.length > 0
    ? productColours
    : (schema.colours ? schema.colours.options : []);
  const allowCustomColour = productColours.length > 0 ? true : !!schema.colours?.allowCustom;
  const colourSection = colourList.length > 0 ? (
    <div className="mt-6" data-testid="variation-colour-block">
      <PillSelect
        label="Colour"
        placeholder="Select Colour"
        value={value.colour || ""}
        options={colourList.map((c) => ({ value: c.name, label: c.name, hex: c.hex }))}
        onChange={(name) => {
          const c = colourList.find((x) => x.name === name);
          set({ colour: name, customColour: "", colourImage: c?.image || "" });
        }}
        testid="variation-colour"
      />
      {allowCustomColour && (
        <input
          data-testid="variation-colour-custom"
          value={value.customColour || ""}
          onChange={(e) => set({ customColour: e.target.value, colour: e.target.value ? "" : value.colour, colourImage: "" })}
          placeholder="Custom colour (e.g., Pantone 18-1663)"
          className="mt-3 w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
        />
      )}
    </div>
  ) : null;

  // ---- fabrics ----
  const fabricSection = schema.fabrics ? (
    <div className="mt-6" data-testid="variation-fabric-block">
      <PillSelect
        label="Fabric"
        placeholder="Select Fabric"
        value={value.fabric || ""}
        options={schema.fabrics.options.map((f) => ({ value: f, label: f }))}
        onChange={(f) => set({ fabric: f, customFabric: "" })}
        testid="variation-fabric"
      />
      {schema.fabrics.allowCustom && (
        <input
          data-testid="variation-fabric-custom"
          value={value.customFabric || ""}
          onChange={(e) => set({ customFabric: e.target.value, fabric: e.target.value ? "" : value.fabric })}
          placeholder="Custom fabric (e.g., Egyptian Giza 45)"
          className="mt-3 w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
        />
      )}
    </div>
  ) : null;

  // ---- closure / header / bagType / designStyle / monogramFont ----
  const dropdownBlock = (label, key, options, placeholder) => (
    <div className="mt-6" data-testid={`variation-${key}-block`}>
      <PillSelect
        label={label}
        placeholder={placeholder || `Select ${label}`}
        value={value[key] || ""}
        options={options.map((o) => ({ value: o, label: o }))}
        onChange={(o) => set({ [key]: o })}
        testid={`variation-${key}`}
      />
    </div>
  );

  const closureSection = schema.closures ? dropdownBlock("Closure", "closure", schema.closures) : null;
  const headerSection = schema.header ? dropdownBlock("Header style", "header", schema.header.options) : null;
  const bagTypeSection = schema.bagType ? dropdownBlock("Bag type", "bagType", schema.bagType.options) : null;
  const designStyleSection = schema.designStyle ? dropdownBlock("Design style", "designStyle", schema.designStyle.options) : null;
  const monogramFontSection = schema.monogramFont ? dropdownBlock("Monogram font", "monogramFont", schema.monogramFont.options) : null;

  const chart = schema.sizeChart ? SIZE_CHARTS[schema.sizeChart] : null;

  return (
    <>
      {productSizeSection}
      {!productSizeSection && sizeSection}
      {colourSection}
      {fabricSection}
      {closureSection}
      {headerSection}
      {bagTypeSection}
      {designStyleSection}
      {monogramFontSection}

      {/* Universal — Custom design brief */}
      <div className="mt-10 border-t border-hairline pt-6" data-testid="variation-custom-design-block">
        <div className="overline text-gold mb-2 flex items-center gap-2">
          <PenLine size={12} strokeWidth={1.6} /> Custom design · optional
        </div>
        <p className="text-forest/60 text-xs mb-2">Share sketches, thread counts, edging, embroidery notes — anything unique to your order.</p>
        <textarea
          data-testid="variation-custom-design"
          rows={3}
          value={value.customDesign || ""}
          onChange={(e) => set({ customDesign: e.target.value })}
          placeholder="e.g., Antique-gold piping on borders, sateen finish, embroidered monogram on pillow cases"
          className="w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
        />
      </div>

      {/* Universal — Monogram add-on */}
      <div className="mt-6 border border-hairline rounded-lg p-4 bg-cream" data-testid="variation-monogram-block">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="variation-monogram-toggle"
            checked={!!value.monogramOn}
            onChange={(e) => set({ monogramOn: e.target.checked })}
            className="mt-1 accent-forest"
          />
          <div className="flex-1">
            <div className="font-display text-forest text-lg leading-none flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.6} className="text-champagne" />
              Add monogram
              {monogramPrice > 0 && (
                <span
                  className="ml-1 text-gold text-sm font-normal tabnum"
                  data-testid="monogram-price-label"
                >
                  {formatPrice ? `+${formatPrice(monogramPrice)}` : `+₹${monogramPrice}`}
                </span>
              )}
            </div>
            <p className="text-forest/60 text-xs mt-1">
              {monogramPrice > 0
                ? "In-house embroidery. Share initials, font and thread colour — added per order."
                : "Free with every order. Share initials, font, thread colour — we'll craft it in-house."}
            </p>
          </div>
        </label>
        <AnimatePresence>
          {value.monogramOn && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 grid grid-cols-2 gap-2 overflow-hidden"
            >
              <input
                data-testid="variation-monogram-initials"
                value={value.monogramInitials || ""}
                onChange={(e) => set({ monogramInitials: e.target.value.toUpperCase().slice(0, 4) })}
                placeholder="Initials (e.g. ADR)"
                className="col-span-1 bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest uppercase tracking-widest"
              />
              <input
                data-testid="variation-monogram-thread"
                value={value.monogramThread || ""}
                onChange={(e) => set({ monogramThread: e.target.value })}
                placeholder="Thread colour (e.g. Antique Gold)"
                className="col-span-1 bg-white border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
              />
              <div className="col-span-2 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setMonogramModal(true)}
                  disabled={!value.monogramInitials || value.monogramInitials.length < 3}
                  data-testid="open-monogram-preview"
                  className="inline-flex items-center gap-2 overline text-white bg-gold rounded-full px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed hover:bg-champagne hover:text-forest transition-colors tracking-[2px]"
                >
                  <Wand2 size={12} strokeWidth={1.8} /> AI Preview Fonts
                </button>
                {value.monogramStyle && (
                  <div className="flex items-center gap-2 border border-hairline rounded-lg px-3 py-2 bg-white" data-testid="monogram-preview-thumb">
                    <span
                      className="text-3xl leading-none"
                      style={{
                        fontFamily: value.monogramFontFamily || '"Cormorant Garamond", serif',
                        color: value.monogramThreadHex || "#1B4332",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {value.monogramInitials || "ABC"}
                    </span>
                    <div className="text-forest/60 text-[11px] max-w-[180px]">
                      <div className="text-forest text-xs font-medium">{value.monogramStyle}</div>
                      <div>{value.monogramThread || "Thread TBD"}</div>
                    </div>
                  </div>
                )}
                {(!value.monogramInitials || value.monogramInitials.length < 3) && (
                  <span className="text-forest/40 text-[11px] italic">Type 3+ initials above to enable AI preview.</span>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MonogramPreviewModal
        open={monogramModal}
        defaultLetters={value.monogramInitials}
        defaultThread={value.monogramThread}
        onClose={() => setMonogramModal(false)}
        onSelect={(payload) => set({ ...payload, monogramInitials: payload.monogramLetters })}
      />

      {/* Size Chart modal */}
      <AnimatePresence>
        {chartOpen && chart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setChartOpen(false)}
            data-testid="size-chart-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-2xl border border-hairline overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-hairline flex items-center justify-between bg-cream">
                <div>
                  <div className="overline text-gold">§ Reference</div>
                  <div className="font-display text-forest text-2xl">{chart.title}</div>
                </div>
                <button data-testid="size-chart-close" onClick={() => setChartOpen(false)} className="text-forest/60 hover:text-forest">
                  <X size={18} strokeWidth={1.6} />
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <table className="w-full text-sm text-forest">
                  <thead>
                    <tr className="text-left border-b border-hairline">
                      {chart.columns.map((c) => (
                        <th key={c} className="overline text-[10px] text-forest/60 py-2 pr-4">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chart.rows.map((row, i) => (
                      <tr key={i} className="border-b border-hairline/60">
                        {row.map((cell, j) => (
                          <td key={j} className="py-2 pr-4 tabnum">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-forest/50 text-xs mt-4 italic">Measurements are for the finished cover / garment. Custom sizes are always welcome — mention dimensions on checkout.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * Serialise the selection into a human-readable summary line, used in cart /
 * WhatsApp / order records. Returns "" when nothing meaningful is selected.
 */
export const summariseVariation = (v = {}) => {
  const bits = [];
  if (v.size) bits.push(`Size: ${v.size}${v.sizeRegion ? ` (${v.sizeRegion})` : ""}`);
  else if (v.customSize) bits.push(`Custom size: ${v.customSize}`);
  if (v.colour) bits.push(`Colour: ${v.colour}`);
  else if (v.customColour) bits.push(`Custom colour: ${v.customColour}`);
  if (v.fabric) bits.push(`Fabric: ${v.fabric}`);
  else if (v.customFabric) bits.push(`Custom fabric: ${v.customFabric}`);
  if (v.closure) bits.push(`Closure: ${v.closure}`);
  if (v.header) bits.push(`Header: ${v.header}`);
  if (v.bagType) bits.push(`Type: ${v.bagType}`);
  if (v.designStyle) bits.push(`Style: ${v.designStyle}`);
  if (v.monogramFont) bits.push(`Font: ${v.monogramFont}`);
  if (v.monogramOn) bits.push(`Monogram: ${v.monogramInitials || "TBD"}${v.monogramThread ? ` · ${v.monogramThread}` : ""}`);
  if (v.customDesign) bits.push(`Custom brief: ${v.customDesign}`);
  return bits.join(" · ");
};
